import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../../../utils/axios";
import dayjs from "dayjs";
import AdminSidebar from "../../../components/AdminSidebar";
import LoadingComponent from "../../../components/LoadingComponent";
import NoData from "../../../components/NoData";
import Button from "../../../components/Button";
import {
  formatCoveragePeriod,
  normalizePaidMonths,
} from "../../../utils/paymentCoverage";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";

const QUARTER_BADGE = {
  Q1: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Q2: "bg-sky-100 text-sky-700 border-sky-200",
  Q3: "bg-amber-100 text-amber-700 border-amber-200",
  Q4: "bg-violet-100 text-violet-700 border-violet-200",
};

const EMPTY_FILTERS = {
  client_name: "",
  serial_number: "",
  quarter: "",
  year: "",
};

export default function RentalPaymentsOverview() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const perPage = 20;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["all-rental-payments", page, applied],
    queryFn: async () => {
      const params = new URLSearchParams({ page, per_page: perPage });
      Object.entries(applied).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals-payments?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const payments = data?.data?.data || [];
  const currentPage = data?.data?.current_page || 1;
  const lastPage = data?.data?.last_page || 1;
  const total = data?.data?.total || 0;
  const from = data?.data?.from || 0;
  const to = data?.data?.to || 0;

  const applyFilters = () => {
    setApplied(filters);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const onFilterChange = (key) => (e) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") applyFilters();
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(lastPage, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue min-h-screen pb-12">
        <div className="max-w-7xl mx-auto">
          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-6 gap-4">
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Rental Payments History
            </h1>
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-bluePrimary animate-pulse" />
              <span className="text-sm font-semibold text-gray-700">
                {total} payment{total !== 1 ? "s" : ""} recorded
              </span>
            </div>
          </div>

          {/* ── Filters ── */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Client
                </label>
                <input
                  type="text"
                  value={filters.client_name}
                  onChange={onFilterChange("client_name")}
                  onKeyDown={handleKeyDown}
                  placeholder="Client name"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bluePrimary/40"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  value={filters.serial_number}
                  onChange={onFilterChange("serial_number")}
                  onKeyDown={handleKeyDown}
                  placeholder="Serial number"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bluePrimary/40"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Quarter
                </label>
                <select
                  value={filters.quarter}
                  onChange={onFilterChange("quarter")}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-bluePrimary/40"
                >
                  <option value="">All quarters</option>
                  <option value="Q1">Q1 · Jan – Mar</option>
                  <option value="Q2">Q2 · Apr – Jun</option>
                  <option value="Q3">Q3 · Jul – Sep</option>
                  <option value="Q4">Q4 · Oct – Dec</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Year
                </label>
                <input
                  type="number"
                  value={filters.year}
                  onChange={onFilterChange("year")}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. 2026"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bluePrimary/40"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button variant="secondary" size="sm" onClick={resetFilters}>
                Reset
              </Button>
              <Button
                size="sm"
                icon={<MagnifyingGlassIcon className="h-4 w-4" />}
                onClick={applyFilters}
              >
                Search
              </Button>
            </div>
          </div>

          {/* ── Table ── */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingComponent />
            </div>
          ) : payments.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm py-16">
              <NoData message="No payment records found." />
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table
                    className={`min-w-full divide-y divide-gray-100 transition-opacity duration-200 ${
                      isFetching ? "opacity-50" : "opacity-100"
                    }`}
                  >
                    <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                      <tr>
                        <Th>Client</Th>
                        <Th>Serial Number</Th>
                        <Th>Period</Th>
                        <Th>Paid Months</Th>
                        <Th>Covered Period</Th>
                        <Th>Amount</Th>
                        <Th>Payment Date</Th>
                        <Th className="text-right">Details</Th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payments.map((p) => {
                        const paidMonths = normalizePaidMonths(p.paid_months);
                        return (
                          <tr
                            key={p.id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <Td className="font-semibold text-gray-900">
                              {p.client_name || "—"}
                            </Td>
                            <Td>{p.serial_number || p.device_serial_no || "—"}</Td>
                            <Td>
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${
                                  QUARTER_BADGE[p.quarter] ||
                                  "bg-gray-100 text-gray-700 border-gray-200"
                                }`}
                              >
                                {p.quarter} · {p.year}
                              </span>
                            </Td>
                            <Td>
                              <div className="flex flex-wrap gap-1 max-w-[220px]">
                                {paidMonths.length > 0
                                  ? paidMonths.map((m, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600"
                                      >
                                        {m}
                                      </span>
                                    ))
                                  : "—"}
                              </div>
                            </Td>
                            <Td className="whitespace-nowrap font-medium text-gray-700">
                              {formatCoveragePeriod(p.paid_months, p.year)}
                            </Td>
                            <Td className="font-bold text-gray-900 whitespace-nowrap">
                              {p.total_amount != null
                                ? `CHF ${parseFloat(p.total_amount).toFixed(2)}`
                                : "—"}
                            </Td>
                            <Td className="whitespace-nowrap">
                              {p.payment_date
                                ? dayjs(p.payment_date).format("DD MMM YYYY")
                                : "—"}
                            </Td>
                            <Td className="text-right">
                              <button
                                onClick={() =>
                                  navigate(
                                    `/admin/rentals/${p.device_rental_id}/payments`
                                  )
                                }
                                className="text-sm font-semibold text-bluePrimary hover:underline"
                              >
                                View
                              </button>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Pagination ── */}
              {lastPage > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 bg-white rounded-xl border border-gray-100 shadow-sm px-6 py-4 gap-4">
                  <span className="text-sm text-gray-500">
                    Showing{" "}
                    <span className="font-semibold text-gray-700">{from}</span>–
                    <span className="font-semibold text-gray-700">{to}</span> of{" "}
                    <span className="font-semibold text-gray-700">{total}</span>{" "}
                    payments
                  </span>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage <= 1}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>

                    {getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`min-w-[40px] h-10 rounded-lg text-sm font-medium transition-all duration-150 ${
                          pageNum === currentPage
                            ? "bg-bluePrimary text-white shadow-md shadow-blue-200"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}

                    <button
                      onClick={() =>
                        setPage((prev) => Math.min(lastPage, prev + 1))
                      }
                      disabled={currentPage >= lastPage}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminSidebar>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-sm text-gray-700 ${className}`}>{children}</td>;
}
